import { Component, OnInit } from '@angular/core';
import { AHRService } from '../../../domain/data-access-interfaces';
import { SupervisorDirectReports } from 'app/domain';
import { ConsoleService } from '../../../widgets/console.service';
import {FormControl} from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { DossierExportDataModalComponent } from '../../../widgets/dossier-export-data/dossier-export-data-modal.component';
import { SUPERVISOR_REVIEW_LOOKBACK_YEARS } from 'app/shared/constants';

@Component({
    selector: 'my-direct-reports',
    templateUrl: './my-direct-reports.component.html',
    styleUrls: ['./my-direct-reports.component.scss']
})
export class MyDirectReportsComponent implements OnInit {

    directReports: SupervisorDirectReports[] = [];
    filteredDirectReports: SupervisorDirectReports[] = [];
    loading: boolean = true;
    error: boolean = false;
    infoPanelOpenState: boolean = false;
    participationOptions = [{ id: 'WritesReview', label: 'Write Review' }, { id: 'ReadOnly', label: 'Read-only' }];
    supervisorRoles = new FormControl([]);
    myRoles: Set<string>;
    private readonly ROLE_ORDER = ['Primary Supervisor', 'Secondary Supervisor', 'Additional Supervisor'];
    
    constructor(
        private readonly svc: AHRService,
        private readonly console: ConsoleService,
        private matDialog: MatDialog
    ) {}

    ngOnInit() {
        this.svc.getDirectReportsForCurrentUser().subscribe({
            next: (d) => {
                this.directReports = d;
                this.filteredDirectReports = [...d];
                this.console.log(this.directReports);

                // Extract unique roles and sort by ROLE_ORDER (case-insensitive).
                // Roles matching ROLE_ORDER appear first in the specified order.
                // Unmatched roles are appended at the end, sorted alphabetically.
                const uniqueRoles = Array.from(new Set(
                    d.map(r => r.supervisorTitle)
                    .filter(title => !!title)
                    .map(title => this.normalizeSupervisorTitle(title))
                )).sort((a, b) => {
                    const aIndex = this.ROLE_ORDER.findIndex(role => role.toLowerCase() === a.toLowerCase());
                    const bIndex = this.ROLE_ORDER.findIndex(role => role.toLowerCase() === b.toLowerCase());
                    const aOrder = aIndex === -1 ? this.ROLE_ORDER.length : aIndex;
                    const bOrder = bIndex === -1 ? this.ROLE_ORDER.length : bIndex;
                    if (aOrder !== bOrder) {
                        return aOrder - bOrder;
                    }
                    return a.localeCompare(b);
                });
                this.myRoles = new Set(uniqueRoles);

                this.loading = false;
            },
            error: (err) => {
                this.console.error('Failed to load direct reports', err);
                this.error = true;
                this.loading = false;
            }
        });

        this.supervisorRoles.valueChanges.subscribe(selectedRoles => {
            if (!selectedRoles || selectedRoles.length === 0) {
                // No filter selected, show all
                this.filteredDirectReports = [...this.directReports];
            } else {
                // Filter by selected roles
                this.filteredDirectReports = this.directReports.filter(d => 
                    selectedRoles.includes(this.normalizeSupervisorTitle(d.supervisorTitle))
                );
            }
        });
    }
    
    getDirectReportCount(): string {
        const totalCount = this.directReports.length;
        const filteredCount = this.filteredDirectReports.length;

        return filteredCount === totalCount
            ? `${totalCount} Direct Report${totalCount !== 1 ? 's' : ''}`
            : `Showing ${filteredCount} of ${totalCount} Direct Reports`;
    }

    private getSupervisorTitle(originalIndex: number): string {
        if (originalIndex === 0) return 'Primary';
        if (originalIndex === 1) return 'Secondary';
        return 'Additional';
    }

    private normalizeSupervisorTitle(title: string): string {
        if (title === 'Tertiary Supervisor') return 'Additional Supervisor';
        return title;
    }

    getSuperviorTitleAndParticipation(report: SupervisorDirectReports): { title: string; reviewParticipation: string } {

        if (!report.supervisors) return {"title": this.normalizeSupervisorTitle(report.supervisorTitle), "reviewParticipation": 'N/A'};

        const supervisor = report.supervisors.find(s => s.details.id === report.supervisorUserId);
        
        if (!supervisor) return {"title": this.normalizeSupervisorTitle(report.supervisorTitle), "reviewParticipation": 'N/A'};
        
        const index = report.supervisors.findIndex(orig => orig.details.id === supervisor.details.id);
        
        return { title: this.getSupervisorTitle(index), reviewParticipation: this.participationOptions.find(p => p.id === supervisor.reviewParticipation)?.label || supervisor.reviewParticipation };
    }

    getOtherSupervisors(report: SupervisorDirectReports): { name: string; title: string; reviewParticipation: string }[] {

        if (!report.supervisors) return [];

        return report.supervisors
            .filter(s => s.details.id !== report.supervisorUserId)
            .map(s => {
                const index = report.supervisors.findIndex(orig => orig.details.id === s.details.id);
                return {
                    name: s.details.name,
                    title: this.getSupervisorTitle(index),
                    reviewParticipation: this.participationOptions.find(p => p.id === s.reviewParticipation)?.label || s.reviewParticipation
                };
            });
    }

    //Open export data modal
    openExportDataDialog(userId){
        this.console.log("userId: " + userId);
        let minDate = new Date();
        let maxDate = new Date();
        minDate.setFullYear(minDate.getFullYear() - SUPERVISOR_REVIEW_LOOKBACK_YEARS);
        maxDate.setFullYear(maxDate.getFullYear() + 1);
        this.matDialog.open(DossierExportDataModalComponent, {
            width:'500px',
            disableClose: true,
            data: { minDate: minDate,
                    maxDate: maxDate,
                    userId: userId},
            hasBackdrop: true
        });
    }
}